import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, FileJson } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API } from "../lib/api";

// Last Monday → Sunday (local), as YYYY-MM-DD
export function lastWeekRange() {
  const today = new Date();
  const lastSunday = new Date(today);
  lastSunday.setDate(today.getDate() - ((today.getDay() + 7) % 7 || 7));
  const monday = new Date(lastSunday);
  monday.setDate(lastSunday.getDate() - 6);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: iso(monday), end: iso(lastSunday) };
}

export function WeeklyReportExportDialog({ open, onOpenChange }) {
  const [range, setRange] = useState(lastWeekRange);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const url = `${API}/admin/weekly-report-export?start=${range.start}&end=${range.end}`;
      let res = await fetch(url, { credentials: "include" });
      if (res.status === 202) {
        const { job_id } = await res.json();
        for (let i = 0; i < 90; i++) {           // poll up to ~3 min while the backend pulls live box scores
          await new Promise(r => setTimeout(r, 2000));
          const job = await fetch(`${API}/admin/weekly-report-export/jobs/${job_id}`, { credentials: "include" }).then(r => r.json());
          if (job.status === "failed") throw new Error(job.error || "Export job failed");
          if (job.status === "done") break;
        }
        res = await fetch(url, { credentials: "include" });
        if (res.status === 202) throw new Error("Still building — try again in a minute");
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), { href, download: `weekly-report-${range.start}_to_${range.end}.json` });
      a.click();
      URL.revokeObjectURL(href);
      toast.success("Weekly report data downloaded", { description: `${range.start} → ${range.end}` });
      onOpenChange(false);
    } catch (e) { toast.error("Export failed", { description: e.message }); }
    finally { setBusy(false); }
  };

  const field = "h-10 px-3 bg-[#0a0a0a] border border-white/10 text-sm text-white font-mono focus:outline-none focus:border-[var(--org-accent)]";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-white/10 text-white max-w-md" data-testid="weekly-report-export-dialog">
        <DialogHeader>
          <DialogTitle className="text-[11px] uppercase tracking-[0.3em] font-bold text-[var(--org-accent)]">Export Weekly Report Data</DialogTitle>
          <DialogDescription className="text-[#a0a0a0] text-sm">
            One JSON file: every college / pro player's season line plus every box score in the window. Defaults to last Monday – Sunday.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[10px] uppercase tracking-[0.25em] text-[#757575] font-bold">Start
            <input type="date" value={range.start} onChange={(e) => setRange(r => ({ ...r, start: e.target.value }))} className={`${field} mt-1 w-full`} data-testid="weekly-report-start" />
          </label>
          <label className="text-[10px] uppercase tracking-[0.25em] text-[#757575] font-bold">End
            <input type="date" value={range.end} onChange={(e) => setRange(r => ({ ...r, end: e.target.value }))} className={`${field} mt-1 w-full`} data-testid="weekly-report-end" />
          </label>
        </div>
        <button onClick={download} disabled={busy || !range.start || !range.end || range.start > range.end}
          className="mt-2 h-11 w-full inline-flex items-center justify-center gap-2 bg-[var(--org-accent)] text-black text-[11px] uppercase tracking-[0.25em] font-bold disabled:opacity-50"
          data-testid="weekly-report-download-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileJson className="w-4 h-4" />}
          {busy ? "Building (live stats, ~30–60s)…" : "Download JSON"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
